// Library Methods
import { ReactNode } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

type FigureRowType = {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
};

export const FigureRow = ({ label, value, sub }: FigureRowType) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "1px",
      flex: "0 1 calc(33.333% - 11px)",
      minWidth: "140px",
    }}
  >
    <Typography fontSize="12px" color="#5B6D7A">
      {label}
    </Typography>
    <Typography fontSize="12px" component="div">
      {value}
    </Typography>
    {sub && (
      <Box fontSize="12px" color="#5B6D7A">
        {sub}
      </Box>
    )}
  </Box>
);

// Matches FigureRow's own minWidth, so a column never gets narrower than a figure wants to be.
const MIN_FIGURE_WIDTH = "140px";
const FIGURE_GAP = "8px";

type FiguresType = {
  children: ReactNode;
  // Up to this many figures per row, dropping to fewer when they won't fit at their minimum width.
  maxColumns?: number;
};

export const Figures = ({ children, maxColumns }: FiguresType) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: FIGURE_GAP,
      // Each column is at least the figure minimum and at least 1/maxColumns of the row, so
      // auto-fit can never fit more than maxColumns, and fits fewer once the row gets narrow.
      ...(maxColumns && {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(max(${MIN_FIGURE_WIDTH}, calc((100% - ${
          maxColumns - 1
        } * ${FIGURE_GAP}) / ${maxColumns})), 1fr))`,
        "& > *": { minWidth: 0 },
      }),
    }}
  >
    {children}
  </Box>
);
